import { logException } from "@outschool/sentry/dist/client";
import { useEffect, useState } from "react";

import { waitForVar } from "../../waitForVar";

/**
 * useScript hook
 *
 * https://usehooks.com/useScript
 */
export default function useScript<T extends keyof Window>(
  src: string,
  variableName?: T
) {
  const [scriptVariable, setScriptVariable] = useState<Window[T]>();
  useEffect(
    () => {
      // Allow falsy src value if waiting on other data needed for
      // constructing the script URL passed to this hook.
      if (!src) {
        return;
      }

      // Fetch existing script element by src
      // It may have been added by another intance of this hook
      let script = document.querySelectorAll<HTMLScriptElement>(
        `script[src="${src}"]`
      )[0];

      if (!script) {
        // Create script
        script = document.createElement("script");
        script.src = src;
        script.async = true;
        // Add script to document body
        document.body.appendChild(script);
      }
    },
    [src] // Only re-run effect if script src changes
  );

  useEffect(() => {
    async function waitForVariable() {
      if (!variableName) {
        return;
      }
      try {
        setScriptVariable(await waitForVar(variableName));
      } catch (err) {
        logException(err);
      }
    }
    waitForVariable();
  }, [variableName]);

  return scriptVariable;
}
